import { PlusOutlined } from '@ant-design/icons';
import type { OrgGroupRow, OrgGroupSaveRequest, OrgGroupType, OrgRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { checkShortName } from '@hq/utils';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Drawer,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { LogoField, RegionAddressFields, COL, sumWidth, ellipsisCell, TipText } from '@hq/ui-kit';
import type { HqApi } from '@hq/api-sdk';

const GROUP_TYPE_META: Record<OrgGroupType, { text: string; color: string }> = {
  MEDICAL_COMMUNITY: { text: '医共体', color: 'geekblue' },
  GROUP: { text: '集团', color: 'purple' }
};

interface GroupFormValues {
  groupCode: string;
  groupName: string;
  /** 简称：2–6 个汉字。 */
  groupShortName: string;
  logoUrl?: string;
  groupType: OrgGroupType;
  leadOrgId?: string;
  /** 级联显示值，落库走 provinceCode/cityCode/districtCode。 */
  regionCodes?: string[];
  provinceCode?: string;
  cityCode?: string;
  districtCode?: string;
  address?: string;
  lng?: number | null;
  lat?: number | null;
  contractNo?: string;
  remark?: string;
}

/**
 * 医共体 / 集团维护（`org_group`）：统招分签的归口单位。
 * 机构在「机构」页签里通过「所属医共体」挂靠；有成员时不允许删除。
 */
export function OrgGroupPanel({ hq }: { hq: HqApi }) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<OrgGroupRow | null>(null);
  /** 查看成员机构的医共体（成员 = org_institution.group_id 指向它） */
  const [memberOf, setMemberOf] = useState<OrgGroupRow | null>(null);
  const [form] = Form.useForm<GroupFormValues>();

  const groups = useQuery({
    queryKey: ['ops', 'org-group', 'list'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.orgGroup.list()
  });

  const orgs = useQuery({
    queryKey: ['ops', 'org', 'options'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.org.list(),
    staleTime: 60 * 1000
  });

  // 成员清单：按 group_id 过滤机构（成员 = 归口到本医共体的机构）
  const members = useQuery({
    queryKey: ['ops', 'org-group', 'members', memberOf?.id],
    queryFn: () => hq.org.list({ groupId: memberOf!.id }),
    enabled: Boolean(memberOf)
  });

  const save = useMutation({
    mutationFn: (body: OrgGroupSaveRequest) => hq.orgGroup.save(body),
    onSuccess: async (result) => {
      message.success(result.updated ? '医共体已更新' : '医共体已新增');
      setOpen(false);
      setEditing(null);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['ops', 'org-group'] });
      await queryClient.invalidateQueries({ queryKey: ['ops', 'org'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败，请稍后重试');
    }
  });

  const remove = useMutation({
    mutationFn: (id: string) => hq.orgGroup.remove(id),
    onSuccess: async () => {
      message.success('医共体已删除');
      await queryClient.invalidateQueries({ queryKey: ['ops', 'org-group'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '删除失败，请稍后重试');
    }
  });

  const rows = groups.data ?? [];
  const error = groups.error instanceof ApiError ? groups.error.friendlyMessage : null;

  const orgOptions = (orgs.data ?? []).map((row: OrgRow) => ({
    value: row.id,
    label: `${row.orgName}（${row.orgShortName ?? '-'}）`
  }));

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({ groupType: 'MEDICAL_COMMUNITY' });
    setOpen(true);
  };

  const openEdit = (row: OrgGroupRow) => {
    setEditing(row);
    form.setFieldsValue({
      groupCode: row.groupCode,
      groupName: row.groupName,
      groupShortName: row.groupShortName ?? '',
      logoUrl: row.logoUrl ?? undefined,
      groupType: row.groupType,
      leadOrgId: row.leadOrgId === '0' ? undefined : row.leadOrgId,
      // 级联显示值由 RegionAddressFields 按 districtCode 自动补全
      regionCodes: undefined,
      provinceCode: row.provinceCode ?? undefined,
      cityCode: row.cityCode ?? undefined,
      districtCode: row.districtCode ?? undefined,
      address: row.address ?? undefined,
      lng: row.lng,
      lat: row.lat,
      contractNo: row.contractNo ?? undefined,
      remark: row.remark ?? undefined
    });
    setOpen(true);
  };

  const columns: ColumnsType<OrgGroupRow> = [
    {
      title: '医共体 / 集团',
      // 单行展示：编码/地址/坐标收进悬浮提示，避免行高被拉高
      render: (_, row) => (
        <Tooltip
          placement="topLeft"
          title={[
            row.groupCode,
            row.address,
            row.lng != null && row.lat != null ? `${Number(row.lng).toFixed(5)}, ${Number(row.lat).toFixed(5)}` : null
          ]
            .filter(Boolean)
            .join(' · ')}
        >
          <Space size={6} align="center">
            {row.logoUrl ? (
              <img
                src={row.logoUrl}
                alt=""
                style={{ height: 20, width: 20, objectFit: 'contain', borderRadius: 3, border: '1px solid #f0f0f0', flex: '0 0 auto' }}
              />
            ) : null}
            <span>{row.groupName}</span>
            {row.groupShortName ? <Tag style={{ marginInlineEnd: 0 }}>{row.groupShortName}</Tag> : null}
            <Tag color={GROUP_TYPE_META[row.groupType]?.color ?? 'default'} style={{ marginInlineEnd: 0 }}>
              {GROUP_TYPE_META[row.groupType]?.text ?? row.groupType}
            </Tag>
          </Space>
        </Tooltip>
      )
    },
    {
      title: '牵头机构',
      dataIndex: 'leadOrgName',
      width: COL.dept,
      ellipsis: { showTitle: false },
      render: (value: string | null) => (value ? <TipText text={value} /> : '—')
    },
    {
      title: '统招分签合同号',
      dataIndex: 'contractNo',
      width: COL.bizNo,
      ellipsis: { showTitle: false },
      render: (value: string | null) => (value ? <TipText text={value} /> : '-')
    },
    {
      title: '成员机构',
      dataIndex: 'memberCount',
      width: COL.qty,
      align: 'right',
      render: (value: number, row) =>
        value > 0 ? (
          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setMemberOf(row)}>
            {value}
          </Button>
        ) : (
          0
        )
    },
    { title: '备注', dataIndex: 'remark', width: COL.dept, ellipsis: { showTitle: false }, render: ellipsisCell('remark') },
    {
      title: '操作',
      width: COL.action2,
      fixed: 'right',
      render: (_, row) => (
        <Space size={0}>
          <Button type="link" size="small" onClick={() => openEdit(row)}>
            编辑
          </Button>
          <Popconfirm
            title="删除该医共体？"
            description={row.memberCount > 0 ? `还有 ${row.memberCount} 家机构挂靠，请先调整归属` : '删除后不可恢复'}
            okText="删除"
            cancelText="取消"
            onConfirm={() => remove.mutate(row.id)}
          >
            <Button type="link" size="small" danger loading={remove.isPending}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<PlusOutlined />} type="primary" onClick={openCreate}>
          新建医共体
        </Button>
      </Space>

      {error ? <Alert type="error" showIcon style={{ marginBottom: 12 }} message="加载失败" description={error} /> : null}

      <Table<OrgGroupRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={groups.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: '暂无医共体，点击「新建医共体」录入' }}
      />

      <Modal
        title={editing ? '编辑医共体' : '新建医共体'}
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditing(null);
        }}
        onOk={() => form.submit()}
        confirmLoading={save.isPending}
        width={760}
        destroyOnHidden
      >
        <Form<GroupFormValues>
          form={form}
          layout="vertical"
          onFinish={(values) =>
            save.mutate({
              id: editing?.id,
              groupCode: values.groupCode,
              groupName: values.groupName,
              groupShortName: values.groupShortName,
              logoUrl: values.logoUrl,
              groupType: values.groupType,
              leadOrgId: values.leadOrgId ?? '0',
              provinceCode: values.provinceCode,
              cityCode: values.cityCode,
              districtCode: values.districtCode,
              address: values.address,
              lng: values.lng ?? null,
              lat: values.lat ?? null,
              contractNo: values.contractNo,
              remark: values.remark
            })
          }
        >
          <Space size={16} wrap style={{ width: '100%' }} align="start">
            <Form.Item
              name="groupCode"
              label="编码"
              rules={[
                { required: true, message: '请填写编码' },
                { pattern: /^[0-9A-Za-z_-]{2,32}$/, message: '字母、数字、下划线或短横线，2–32 位' }
              ]}
              style={{ width: 220 }}
            >
              <Input maxLength={32} placeholder="如 YGT-330111" />
            </Form.Item>
            <Form.Item
              name="groupName"
              label="名称"
              rules={[{ required: true, message: '请填写名称' }]}
              style={{ width: 340 }}
            >
              <Input maxLength={128} placeholder="如 行政第一人民医院医共体" />
            </Form.Item>
            <Form.Item name="groupType" label="类型" rules={[{ required: true, message: '请选择类型' }]} style={{ width: 180 }}>
              <Select
                options={[
                  { value: 'MEDICAL_COMMUNITY', label: '医共体' },
                  { value: 'GROUP', label: '集团' }
                ]}
              />
            </Form.Item>
            <Form.Item
              name="groupShortName"
              label="简称（2–6 个汉字）"
              rules={[
                { required: true, message: '请填写简称' },
                {
                  validator: (_, value: string) => {
                    const error = checkShortName(value);
                    return error ? Promise.reject(new Error(error)) : Promise.resolve();
                  }
                }
              ]}
              style={{ width: 200 }}
            >
              <Input placeholder="如 富阳医共体" maxLength={6} showCount />
            </Form.Item>
          </Space>

          <Space size={16} wrap style={{ width: '100%' }} align="start">
            <Form.Item
              name="leadOrgId"
              label="牵头机构"
              rules={[{ required: true, message: '请指定牵头机构（成员单位的上级机构）' }]}
              extra="成员单位在机构树上的上级机构将锁定为该牵头机构"
              style={{ width: 380 }}
            >
              <Select allowClear showSearch optionFilterProp="label" placeholder="通常为主院" options={orgOptions} />
            </Form.Item>
            <Form.Item name="contractNo" label="统招分签合同号" style={{ width: 240 }}>
              <Input maxLength={64} />
            </Form.Item>
          </Space>

          <LogoField hq={hq} form={form} label="医共体 / 集团 LOGO" bizType="ORG_GROUP_LOGO" />

          <RegionAddressFields hq={hq} form={form} addressHint="医共体办公地址，用于高德解析坐标与区域统计" />

          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={memberOf ? `成员机构 · ${memberOf.groupName}` : '成员机构'}
        width={760}
        open={Boolean(memberOf)}
        onClose={() => setMemberOf(null)}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="成员单位的上级机构 = 本医共体牵头机构"
          description="成员在「机构管理」里把角色设为「医共体成员」并选择本医共体，上级机构会自动锁定为牵头机构；医共体本身不占机构树的层级。"
        />
        <Table<OrgRow>
          size="small"
          rowKey="id"
          loading={Boolean(memberOf) && members.isPending}
          dataSource={members.data ?? []}
          pagination={false}
          locale={{ emptyText: '暂无成员单位，去「机构管理」把机构角色设为「医共体成员」并选择本医共体' }}
          columns={[
            { title: '机构编码', dataIndex: 'orgCode', width: 150 },
            { title: '机构名称', dataIndex: 'orgName', ellipsis: { showTitle: false }, render: (v: string) => <TipText text={v} /> },
            { title: '简称', dataIndex: 'orgShortName', width: 100, render: (v: string | null) => v ?? '-' },
            { title: '类型', dataIndex: 'instType', width: 90 },
            { title: '角色', dataIndex: 'instRole', width: 140 },
            { title: '层级', dataIndex: 'levelNo', width: 70 },
            {
              title: '上级机构',
              dataIndex: 'parentOrgName',
              width: 200,
              ellipsis: { showTitle: false },
              render: (v: string | null) => (v ? <TipText text={v} /> : '-')
            }
          ]}
        />
      </Drawer>
    </>
  );
}
